import React, {FC, useState, useCallback, useMemo} from "react";
import {makeStyles} from "tss-react/mui";
import {Box} from "@mui/material";
import {
	DataGridPro,
	GridColDef,
	GridFilterModel,
	GridSortModel,
	GridToolbarContainer,
	GridToolbarFilterButton,
	GridToolbar
} from "@mui/x-data-grid-pro";
import {
	useGetAnnouncementEnrollments,
	ANNOUNCEMENT_ENROLLMENTS_MAX_PAGE_SIZE,
	AnnouncementsControllerGetEnrollmentsSortBy,
	type AnnouncementEnrollmentUserDto,
	type AnnouncementsControllerGetEnrollmentsSortBy as EnrollmentsSortByField,
	type AnnouncementsControllerGetEnrollmentsSortDirection as EnrollmentsSortDirection
} from "@/app2/shared/data";

const ENROLLMENT_SORT_FIELDS = new Set<string>(
	Object.values(AnnouncementsControllerGetEnrollmentsSortBy)
);

function toEnrollmentsSortBy(field: string | undefined): EnrollmentsSortByField | undefined {
	if (!field || !ENROLLMENT_SORT_FIELDS.has(field)) return undefined;
	return field as EnrollmentsSortByField;
}

function toEnrollmentsSortDirection(
	sort: string | null | undefined
): EnrollmentsSortDirection | undefined {
	return sort === "asc" || sort === "desc" ? sort : undefined;
}
import {AdminTableColumnsPanel} from "@/components/common/table/AdminTableColumnsPanel";

/** Grid column fields mapped to AnnouncementsControllerGetEnrollmentsParams */
type EnrollmentColumnFilterKey =
	| "firstname"
	| "lastname"
	| "email"
	| "jobTitle"
	| "publishedAt"
	| "completedAt";

interface AnnouncementsEnrollmentsListProps {
	announcementId: number;
}

const GRID_PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

const filterInvalidToolbarProps = <P extends Record<string, unknown>>(props: P) => {
	const {onPointerEnterCapture, onPointerLeaveCapture, onResize, onResizeCapture, ...rest} =
		props;
	return rest;
};

function EnrollmentsGridToolbar(props: React.ComponentProps<typeof GridToolbarContainer>) {
	const containerProps = filterInvalidToolbarProps(props as Record<string, unknown>);
	const buttonProps = filterInvalidToolbarProps(props as Record<string, unknown>);
	return (
		<GridToolbarContainer sx={{py: 0.5}} {...containerProps}>
			<GridToolbarFilterButton {...(buttonProps as any)} />
		</GridToolbarContainer>
	);
}

/** Maps grid filter fields to generated API query params (date filters use *From as single-value filter). */
function filterModelToQueryFilters(model: GridFilterModel): {
	firstname?: string;
	lastname?: string;
	email?: string;
	jobTitle?: string;
	publishedAtFrom?: string;
	completedAtFrom?: string;
} {
	const filters: {
		firstname?: string;
		lastname?: string;
		email?: string;
		jobTitle?: string;
		publishedAtFrom?: string;
		completedAtFrom?: string;
	} = {};
	for (const item of model.items ?? []) {
		const field = item.columnField ?? (item as { field?: string }).field;
		if (!field) continue;
		const raw = item.value;
		if (raw === undefined || raw === null || String(raw).trim() === "") continue;
		const value = String(raw);
		switch (field as EnrollmentColumnFilterKey) {
			case "firstname":
				filters.firstname = value;
				break;
			case "lastname":
				filters.lastname = value;
				break;
			case "email":
				filters.email = value;
				break;
			case "jobTitle":
				filters.jobTitle = value;
				break;
			case "publishedAt":
				filters.publishedAtFrom = value;
				break;
			case "completedAt":
				filters.completedAtFrom = value;
				break;
			default:
				break;
		}
	}
	return filters;
}

const columns: GridColDef<AnnouncementEnrollmentUserDto>[] = [
	{
		field: "firstname",
		type: "string",
		headerName: "First Name",
		flex: 1,
		minWidth: 140,
		valueGetter: ({row}) => row.firstname ?? ""
	},
	{
		field: "lastname",
		type: "string",
		headerName: "Last Name",
		flex: 1,
		minWidth: 140,
		valueGetter: ({row}) => row.lastname ?? ""
	},
	{
		field: "email",
		type: "string",
		headerName: "Email",
		flex: 2,
		minWidth: 200,
		valueGetter: ({row}) => row.email ?? ""
	},
	{
		field: "jobTitle",
		type: "string",
		flex: 1,
		headerName: "Job Title",
		valueGetter: ({row}) => row.jobTitle ?? ""
	},
	{
		field: "publishedAt",
		type: "string",
		sortable: false,
		filterable: false,
		flex: 1,
		headerName: "Delivered",
		valueGetter: (e) =>
			e.value ? new Date(e.value as string).toLocaleDateString() : ""
	},
	{
		field: "completedAt",
		type: "string",
		filterable: false,
		flex: 1,
		headerName: "Seen",
		valueGetter: (e) =>
			e.value ? new Date(e.value as string).toLocaleDateString() : ""
	}
];

const useStyles = makeStyles()(() => ({
	root: {
		width: "100%",
		height: 420,
		display: "flex",
		flexDirection: "column",
		minHeight: 0
	},
	grid: {
		flex: 1,
		minHeight: 0,
		width: "100%"
	}
}));

export const AnnouncementEnrollmentsList: FC<AnnouncementsEnrollmentsListProps> = ({
	announcementId
}) => {
	const {classes} = useStyles();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(25);
	const [sortModel, setSortModel] = useState<GridSortModel>([]);
	const [filterModel, setFilterModel] = useState<GridFilterModel>({items: []});

	const columnFilters = useMemo(() => filterModelToQueryFilters(filterModel), [filterModel]);

	const sortBy = toEnrollmentsSortBy(sortModel[0]?.field);
	const sortDirection = toEnrollmentsSortDirection(sortModel[0]?.sort);

	const {data, isFetching} = useGetAnnouncementEnrollments(announcementId, {
		page,
		pageSize,
		...(sortBy !== undefined && {sortBy}),
		...(sortDirection !== undefined && {sortDirection}),
		...columnFilters
	});

	const rows = data?.data ?? [];
	const totalCount = data?.totalCount ?? 0;

	const handleSortModelChange = useCallback((model: GridSortModel) => {
		setSortModel(model);
		setPage(1);
	}, []);

	const handleFilterModelChange = useCallback((model: GridFilterModel) => {
		setFilterModel(model);
		setPage(1);
	}, []);

	const handlePageChange = useCallback((newPage: number) => {
		setPage(newPage + 1);
	}, []);

	const handlePageSizeChange = useCallback((newPageSize: number) => {
		setPageSize(Math.min(ANNOUNCEMENT_ENROLLMENTS_MAX_PAGE_SIZE, Math.max(1, newPageSize)));
		setPage(1);
	}, []);

	return (
		<Box className={classes.root}>
			<DataGridPro<AnnouncementEnrollmentUserDto>
				className={classes.grid}
				rows={rows}
				columns={columns}
				getRowId={(row) =>
					row.userId != null ? row.userId : `email:${row.email ?? "unknown"}`
				}
				loading={isFetching}
				pagination
				paginationMode="server"
				filterMode="server"
				sortingMode="server"
				rowCount={totalCount}
				page={page - 1}
				pageSize={pageSize}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
				rowsPerPageOptions={[...GRID_PAGE_SIZE_OPTIONS]}
				sortModel={sortModel}
				onSortModelChange={handleSortModelChange}
				filterModel={filterModel}
				onFilterModelChange={handleFilterModelChange}
				components={{Toolbar: GridToolbar, ColumnsPanel: AdminTableColumnsPanel}}
				disableSelectionOnClick
				density="compact"
				checkboxSelection
			/>
		</Box>
	);
};
